import { ReactNode, useRef, useState } from 'react'

import classNames from 'classnames'
import { connect, DataItem } from 'scrivito'

import { TableColumn } from './types'
import { errorToast } from '../Toast'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import { SchemaAttributes } from '@/Data/types'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { useOnEscPress } from '@/hooks/useOnEscPress'
import { isAutomationsItem } from '@/utils/automationsUtils'
import { getDataItemAttributesFlatMap } from '@/utils/CRUD/read/dataItem/getDataItemMap'
import { FieldEnumValueOption } from '@/utils/CRUD/read/dataItem/types'

import './CellInput.scss'

interface CellInputProps {
  dataItem: DataItem
  schema: DataItem
  col: TableColumn
  children: ReactNode
}
export const CellInput: React.FC<CellInputProps> = connect(
  ({ col, dataItem, schema, children }) => {
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const ref = useRef(null)

    const fieldKey = col.accessor

    if (
      (schema.get('attributes') as SchemaAttributes)[fieldKey]?.readonly ||
      isAutomationsItem(dataItem)
    ) {
      return children
    }

    const onEditClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!isEditing) {
        setIsEditing(true)
      }
    }

    const cancelEditing = () => {
      setIsEditing(false)
    }

    useOnClickOutside({
      elementRef: ref,
      onClick: cancelEditing,
    })

    useOnEscPress({
      onEscPress: cancelEditing,
    })

    const onChange = (newValue: FieldEnumValueOption) => {
      const val = newValue.value
      dataItem
        .update({ [fieldKey]: val })
        .then(() => {
          cancelEditing()
        })
        .catch((e) => {
          errorToast(e as Error)
        })
    }

    const dataItemAttributesFlatMap = getDataItemAttributesFlatMap(dataItem)
    const field = dataItemAttributesFlatMap[fieldKey]

    if (!field) {
      return children
    }

    const { value, enumValueOptions } = field

    if (!enumValueOptions) {
      return children
    }

    const option = enumValueOptions.find((o) => o.value === value)

    return (
      <div
        ref={ref}
        onClick={onEditClick}
        className={classNames('cell-input-wrapper', {
          'cell-input-wrapper--editing': isEditing,
        })}
      >
        {isEditing ? (
          <SimpleSelect
            name={`${fieldKey}-select`}
            options={enumValueOptions}
            value={option}
            onChange={onChange}
            openOnMount
            onClose={cancelEditing}
          />
        ) : (
          children
        )}
      </div>
    )
  },
)
